import {
  hasI18nUserLanguageChoice,
  readEdgeLanguageRaw,
} from "@/lib/i18nEdgeSync";
import {
  readLangQueryParam,
  readPersistedUserLanguage,
  resolveQueryLangForDetection,
} from "@/lib/languagePolicy";
import { normalizeLanguageCode } from "@/lib/languages";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { usesSpanishDefaultForSite } from "@/lib/siteGeoPolicy";
import type { CustomDetector } from "i18next-browser-languagedetector";

/**
 * Orden en i18n/config: querystringLang → AR→es → usuario explícito (no AR) → edge → persistido → …
 * usesSpanishDefaultForSite() — ver src/lib/siteGeoPolicy.ts (fuente única con Index).
 */

export const querystringLanguageDetector: CustomDetector = {
  name: "querystringLang",
  lookup() {
    const raw = readLangQueryParam();
    if (!raw) return undefined;
    return resolveQueryLangForDetection(raw);
  },
};

/** Fija español cuando siteGeoPolicy dice bucket “default español” (AR, cookie AR, test AR). */
export const spanishDefaultGeoDetector: CustomDetector = {
  name: "spanishDefaultGeo",
  lookup() {
    if (typeof window === "undefined") return undefined;
    return usesSpanishDefaultForSite() ? "es" : undefined;
  },
};

export const userExplicitLanguageDetector: CustomDetector = {
  name: "userExplicitLanguage",
  lookup() {
    if (typeof window === "undefined") return undefined;
    if (usesSpanishDefaultForSite()) return undefined;
    if (!hasI18nUserLanguageChoice()) return undefined;
    const raw = readPersistedUserLanguage();
    if (!raw) return undefined;
    return normalizeLanguageCode(raw);
  },
};

/** Last session locale (same key as html lang); runs after edge and explicit choice. */
export const persistedUserLanguageDetector: CustomDetector = {
  name: "persistedUserLanguage",
  lookup() {
    if (typeof window === "undefined") return undefined;
    const raw = readPersistedUserLanguage();
    if (!raw) return undefined;
    return normalizeLanguageCode(raw);
  },
};

export const cloudflareEdgeLanguageDetector: CustomDetector = {
  name: "cloudflareEdge",
  lookup() {
    if (typeof window === "undefined") return undefined;
    const server = getServerTemplateConfig();
    let raw = readEdgeLanguageRaw();
    if (usesSpanishDefaultForSite()) {
      const w = server?.language || "es-AR";
      raw = w.toLowerCase().startsWith("es") ? w : "es-AR";
    }
    if (!raw) return undefined;
    return normalizeLanguageCode(raw.replace(/_/g, "-"));
  },
};
